import { InvalidFilterError } from "./InvalidFilterError";
import { isFilterOperator } from "./operators";
import type { FilterFieldSpec, FilterMatcher, FilterSchema, FilterTree } from "./types";

const DEFAULT_MAX_DEPTH = 10;

export interface FilterMatcherOptions {
  readonly maxDepth?: number; // Nesting cap for `$and` / `$or`; defaults to 10.
}

interface CompileScope<Item, FieldName extends string> {
  readonly maxDepth: number;
  readonly schema: FilterSchema<Item, FieldName>;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isSchemaField = <Item, FieldName extends string>(
  schema: FilterSchema<Item, FieldName>,
  key: string
): key is FieldName => Object.prototype.hasOwnProperty.call(schema, key);

const matchEverything = (): boolean => true;

const joinPath = (path: string, key: string): string => (path ? `${path}.${key}` : key);

const compileCondition = <Item>(
  field: FilterFieldSpec<Item>,
  condition: unknown,
  path: string
): FilterMatcher<Item> => {
  if (!isPlainObject(condition) || Object.keys(condition).length === 0) {
    throw new InvalidFilterError(path, "needs at least one operator");
  }
  const tests = Object.entries(condition).map(([operator, operand]) => {
    const operatorPath = joinPath(path, operator);
    if (!isFilterOperator(operator) || !field.operators.includes(operator)) {
      throw new InvalidFilterError(operatorPath, "is not allowed for this field");
    }
    return field.compile(operator, operand, operatorPath);
  });
  return (item) => tests.every((test) => test(item));
};

const compileGroup = <Item, FieldName extends string>(
  group: "$and" | "$or",
  branches: unknown,
  depth: number,
  path: string,
  scope: CompileScope<Item, FieldName>
): FilterMatcher<Item> => {
  if (!Array.isArray(branches) || branches.length === 0) {
    throw new InvalidFilterError(path, "needs a non-empty list");
  }
  const matchers = branches.map((branch, index) =>
    compileNode(branch, depth + 1, `${path}[${index}]`, scope)
  );
  return group === "$and"
    ? (item) => matchers.every((matcher) => matcher(item))
    : (item) => matchers.some((matcher) => matcher(item));
};

const compileNode = <Item, FieldName extends string>(
  node: unknown,
  depth: number,
  path: string,
  scope: CompileScope<Item, FieldName>
): FilterMatcher<Item> => {
  if (depth > scope.maxDepth) {
    throw new InvalidFilterError(path, "is nested too deep");
  }
  if (!isPlainObject(node)) {
    throw new InvalidFilterError(path, "is not an object");
  }
  const entries = Object.entries(node);
  if (entries.length === 0 && depth === 0) {
    return matchEverything;
  }
  const [entry] = entries;
  if (entries.length !== 1 || !entry) {
    throw new InvalidFilterError(path, "needs exactly one key");
  }
  const [key, value] = entry;
  const keyPath = joinPath(path, key);
  if (key === "$and" || key === "$or") {
    return compileGroup(key, value, depth, keyPath, scope);
  }
  if (key.startsWith("$")) {
    throw new InvalidFilterError(keyPath, "is not a group operator");
  }
  if (!isSchemaField(scope.schema, key)) {
    throw new InvalidFilterError(keyPath, "is not a known field");
  }
  return compileCondition(scope.schema[key], value, keyPath);
};

// Checks `filter` against the server's `$filters` rules once and returns a matcher for items.
// Throws `InvalidFilterError` when a rule is broken; a missing filter matches every item.
export const createFilterMatcher = <Item, FieldName extends string, SchemaField extends string>(
  filter: FilterTree<FieldName> | null | undefined,
  schema: FilterSchema<Item, SchemaField>,
  options: FilterMatcherOptions = {}
): FilterMatcher<Item> =>
  filter === null || filter === undefined
    ? matchEverything
    : compileNode(filter, 0, "", { maxDepth: options.maxDepth ?? DEFAULT_MAX_DEPTH, schema });

export const filterItems = <Item, FieldName extends string, SchemaField extends string>(
  items: readonly Item[],
  filter: FilterTree<FieldName> | null | undefined,
  schema: FilterSchema<Item, SchemaField>,
  options?: FilterMatcherOptions
): Item[] => items.filter(createFilterMatcher(filter, schema, options));
